import { FileAddOutlined } from '@ant-design/icons';
import styled from '@emotion/styled';
import { Col, Form, Input, message, Radio, Row, Select } from 'antd';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BENEFIT } from '../../../../constants/define/translation';
import {
  postCreateBenefitRepository,
  putUpdateBenefitRepository,
} from '../../../../repository/mytm/benefit/benefitRepository';
import { appStyles, defaultImage, formScrolling, selectSelector } from '../../../../styles';
import { FormBenefitType, PartnerType, LinkTypeList, LinkType } from '../../../../types/benefit';
import { CONFIG_CONTAINER_NAME, uploadFileToBlob, UploadFileToBlobInterface } from '../../../../utils/azure';
import { ButtonActionPage, SelectOption, SpinLoading } from '../../../atoms';
import { PreviewBenefit } from '../../../molecules/preview/common';
import { DragDropImage } from '../../../molecules/upload';
import { CountryContext } from '../../../../repository/context/countryContext';
import { APP_DEEP_LINK_FEATURES } from '../../../../constants/home';

interface FormBenefitProps {
  record?: FormBenefitType;
  partners?: PartnerType[];
  onRefresh: () => void;
  onCloseModal: () => void;
  setDataRecord: Function;
  setIsOpenModalPartner: Function;
  countrySelected: string;
}

const { Option } = Select;
const azureCDN = process.env.REACT_APP_AZURE_CDN;
const storageAccountName = process.env.REACT_APP_AZURE_BLOB_ACCOUNT_NAME;
const { TextArea } = Input;

export const FormBenefit = (props: FormBenefitProps) => {
  const { t } = useTranslation(BENEFIT);
  const { record, onRefresh, onCloseModal, setDataRecord, partners, setIsOpenModalPartner, countrySelected } = props;
  const [form] = Form.useForm();
  const [isCreateLoading, setCreateLoading] = useState(false);
  const [imageList, setImageList] = useState<UploadFileToBlobInterface[]>([]);
  const [messageErrorUploadImage, setMessageErrorUploadImage] = useState<string>();
  const [selectedImage, setSelectedImage] = useState<number>(0);
  const [dataForm, setDataForm] = useState({});
  const [isDeepLink, setIsDeepLink] = useState(false);
  const countryContext = useContext(CountryContext);

  const countryOptions: any[] = countryContext?.countryList?.map(item => {
    return {
      value: item?.code,
      label: item?.name,
    };
  });

  const DeepLinkList = APP_DEEP_LINK_FEATURES.map(val => {
    return {
      text: val?.value,
      value: val?.ctaLink,
    };
  });

  const defaultValue = {
    title: record?.title,
    subTitle: record?.subTitle,
    buttonList: record?.buttonList || `Let's Go`,
    buttonDetail: record?.buttonDetail || `Take Advantage`,
    description: record?.description,
    partnerId: record?.partnerId,
    url: record?.url,
    disabled: record?.disabled,
    imageUrl: record?.imageUrl,
    linkType: record?.linkType || LinkType.WEB_LINK,
    deepLink: record?.deepLink,
    countries: record ? record?.countries : countrySelected || 'USA',
  };

  const onSetImageList = useCallback((data: UploadFileToBlobInterface[]) => {
    setImageList(data);
  }, []);

  const onSelectedImage = useCallback((index: number) => {
    setSelectedImage(index);
  }, []);

  const onOpenModalPartner = useCallback(() => {
    setIsOpenModalPartner(true);
  }, []);

  const onChangeInput = useCallback(
    e => {
      const newData = form.getFieldsValue();
      if (newData && newData.linkType === LinkType.DEEP_LINK) {
        setIsDeepLink(true);
      } else {
        setIsDeepLink(false);
      }
      setDataForm(old => ({
        ...old,
        ...form.getFieldsValue(),
      }));
    },
    [form],
  );

  const onFinish = useCallback(
    (formValue: FormBenefitType | any) => {
      if (!imageList.length) {
        setMessageErrorUploadImage(t('validate.imageBlank'));
        return;
      }
      setCreateLoading(true);
      const imgList = imageList.filter(img => !img.isImageURL);
      const imgLink = imageList.filter(img => img.isImageURL);
      uploadFileToBlob(imgList, CONFIG_CONTAINER_NAME.UPLOADS)
        .then((images: any[]) => {
          let newImagesCDN = images;
          if (azureCDN) {
            newImagesCDN = images.map(url => url.replace(`${storageAccountName}.blob.core.windows.net`, azureCDN));
          }
          const imageUrl = newImagesCDN && newImagesCDN.length ? newImagesCDN[0] : imgLink.map(v => v.dataURL)[0];

          const countryStr = form.getFieldValue('countries').toString();
          const payloadToPost = {
            ...formValue,
            imageUrl,
            countries: countryStr,
          };
          if (record) {
            return putUpdateBenefitRepository({
              ...payloadToPost,
              id: record?.id,
            })
              .then(() => {
                onRefresh();
                onCloseModal();
                message.success(t('message.success.updateBenefit'));
              })
              .catch(e => {
                setCreateLoading(true);
                message.error(e?.errorMessage);
              })
              .finally(() => {
                setCreateLoading(false);
              });
          }
          return postCreateBenefitRepository(payloadToPost)
            .then(() => {
              onRefresh();
              onCloseModal();
              message.success(t('message.success.createBenefit'));
            })
            .catch(e => {
              setCreateLoading(true);
              message.error(e?.errorMessage);
            });
        })
        .catch(() => {
          setCreateLoading(true);
          message.error(t('message.error.uploadImage'));
        })
        .finally(() => {
          setCreateLoading(false);
          setDataRecord(null);
        });
    },
    [record, imageList],
  );

  useEffect(() => {
    if (record) {
      setImageList([{ dataURL: record?.imageUrl, isImageURL: true }]);
      setSelectedImage(0);
      setDataForm({
        ...record,
        countries: record?.countries?.split(','),
      });

      if (record && record?.linkType === LinkType.DEEP_LINK) {
        setIsDeepLink(true);
      }
    }
  }, [record]);

  return (
    <>
      <SpinLoading isLoading={isCreateLoading} absolute>
        <SwitchContainer>
          <Form form={form} layout="vertical" initialValues={defaultValue} onFinish={onFinish} name="from-Benefit">
            <Row className="form-Benefit-container" gutter={[24, 24]}>
              <StyledSelectImageCol span={9}>
                <DragDropImage
                  {...{
                    imageList,
                    onSetImageList,
                    messageErrorUploadImage,
                    selectedImage,
                    onSelectedImage,
                    maxImage: 1,
                    nodePreview: (
                      <PreviewBenefit
                        values={{
                          ...dataForm,
                          partners,
                          backgroundImage: imageList?.length && imageList[0].dataURL,
                          describeText: t('form.imageUpload'),
                          logoPartner: record?.partner?.partnerBrandImageUrl,
                        }}
                        isPreview
                        height="400px"
                      />
                    ),
                  }}
                />
              </StyledSelectImageCol>
              <StyledFormBenefit span={15}>
                <div className="form-scroll">
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="countries"
                        label={t('form.countries')}
                        rules={[{ required: true, message: t('validate.countryBlank') }]}
                      >
                        <Radio.Group options={countryOptions} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>

                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item
                        name="title"
                        label={t('form.title')}
                        rules={[{ required: true, message: t('validate.titleBlank') }]}
                      >
                        <Input placeholder={t('form.title')} maxLength={255} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[16, 16]}>
                    <Col span={24}>
                      <Form.Item name="subTitle" label={t('form.subTitle')}>
                        <TextArea
                          style={{ overflow: 'hidden' }}
                          rows={3}
                          placeholder={t('form.subTitle')}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24} style={{ position: 'relative' }}>
                      <button className="add-partner" onClick={() => onOpenModalPartner()} type="button">
                        {t('form.addPartner')}
                      </button>
                      <Form.Item
                        style={{ width: '100%' }}
                        name="partnerId"
                        label={t('form.partner')}
                        rules={[{ required: true, message: t('validate.partnerBlank') }]}
                      >
                        <Select
                          showSearch
                          placeholder={t('form.partner')}
                          optionFilterProp="children"
                          filterOption={(input, option: any) =>
                            option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                          }
                          onChange={onChangeInput}
                          allowClear
                        >
                          {partners &&
                            partners.length > 0 &&
                            partners.map((item: any, index: any) => (
                              <Option value={item?.id || ''} key={item.id} className="option-content-product-ecom">
                                {item.title}
                              </Option>
                            ))}
                        </Select>
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row>
                    <Col span={24}>
                      <SelectOption
                        modeForm
                        name="linkType"
                        label={t('form.linkType')}
                        rules={[{ required: true, message: t('validate.linkTypeBlank') }]}
                        values={LinkTypeList}
                        textDisplay="text"
                        onChange={onChangeInput}
                        showSearch
                        allowClear
                      />
                    </Col>
                  </Row>
                  {isDeepLink ? (
                    <Row>
                      <Col span={24}>
                        <SelectOption
                          modeForm
                          name="deepLink"
                          label={t('form.deepLink')}
                          rules={[{ required: true, message: t('validate.deepLinkBlank') }]}
                          values={DeepLinkList}
                          textDisplay="text"
                          onChange={onChangeInput}
                          showSearch
                          allowClear
                        />
                      </Col>
                    </Row>
                  ) : (
                    <Row>
                      <Col span={24}>
                        <Form.Item
                          name="url"
                          label={t('form.link')}
                          rules={[{ required: true, message: t('validate.linkBlank') }]}
                        >
                          <Input placeholder={t('form.link')} maxLength={255} onChange={onChangeInput} />
                        </Form.Item>
                      </Col>
                    </Row>
                  )}
                  <Row>
                    <Col span={24}>
                      <Form.Item name="description" label={t('form.description')}>
                        <TextArea
                          style={{ overflow: 'hidden' }}
                          rows={6}
                          placeholder={t('form.description')}
                          onChange={onChangeInput}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={[12, 12]}>
                    <Col span={12}>
                      <Form.Item
                        name="buttonList"
                        label={t('form.buttonList')}
                        rules={[{ required: true, message: t('validate.buttonList') }]}
                      >
                        <Input placeholder={t('form.buttonList')} maxLength={40} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item
                        name="buttonDetail"
                        label={t('form.buttonDetail')}
                        rules={[{ required: true, message: t('validate.buttonDetail') }]}
                      >
                        <Input placeholder={t('form.buttonDetail')} maxLength={40} onChange={onChangeInput} />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row style={{ justifyContent: 'flex-end', paddingTop: 20 }}>
                    <BtnContainer>
                      <ButtonActionPage
                        loading={isCreateLoading}
                        text={record ? t('form.btnUpdateBenefit') : t('form.btnCreateBenefit')}
                        id="1"
                        className="btn-submit-form-home"
                        htmlType="submit"
                        icon={!record ? <FileAddOutlined /> : undefined}
                      />
                    </BtnContainer>
                  </Row>
                </div>
              </StyledFormBenefit>
            </Row>
          </Form>
        </SwitchContainer>
      </SpinLoading>
    </>
  );
};

const SwitchContainer = styled.div`
  & .ant-form-item-label {
    font-weight: bold;
    font-size: 15px;
  }

  & .form-Benefit-container {
    padding: 1.5rem;

    .form-Benefit__description {
      resize: none;
    }
  }
`;

const BtnContainer = styled.div`
  & .btn-submit-form-home {
    width: 160px;
  }
`;

const StyledSelectImageCol = styled(Col)`
  padding: 0px !important;
  min-height: 400px;
  height: 520px;
  background-color: #efefef;
  position: relative;
  ${defaultImage}
`;

const StyledFormBenefit = styled(Col)`
  ${formScrolling}
  ${selectSelector}

  & input[type='text'],
  & input[type='number'] {
    ${appStyles.inputStyle}
  }

  & .add-partner {
    float: right;
    color: #3490ff;
    font-weight: bold;
    font-size: 14px;
    position: absolute;
    right: 0;
    z-index: 9999;
    background-color: transparent;
    border: none;
    cursor: pointer;
    text-decoration: underline;
    display: inline;
  }

  & .ant-form-item-label {
    font-weight: bold;
    font-size: 16px;
  }

  & button.btn-submit-form-home {
    ${appStyles.commonButtonSubmitted}
  }

  & .ant-form-item {
    margin-bottom: 0.5rem;
  }

  & .ant-picker {
    width: 100%;
    padding: 0;
    ${appStyles.commonBorderRadius}
    & input {
      padding: 4px 0 4px 12px;
    }
    & .ant-picker-suffix {
      font-size: 20px;
      ${appStyles.background.black}
      ${appStyles.color.white}
      padding: 8px;
    }
  }

  & textarea {
    ${appStyles.commonBorderRadius}
  }

  & .ant-input-affix-wrapper {
    padding: 0 11px;
    ${appStyles.commonBorderRadius}
  }

  & .ant-form-item {
    ${appStyles.commonItemMarginBottom}
  }

  & .ant-form-item-explain {
    margin: 0;
  }

  & .ant-picker-clear {
    right: 40px;
  }

  & .ant-descriptions {
    cursor: default;
    & .ant-descriptions-header {
      margin-bottom: 5px;
    }
  }
`;
